// The fourth answer to spouse-pass's shape-1 question — the spouse is
// real, known, married, and will NEVER become a Stewardship contact.
//
// spouse-pass shape 1 asks "Does <Name> belong in a partner's
// household?" and offered three answers before this one: search-and-
// merge (yes, the partner is already in the system), "No — <Name> is
// on their own" (lives-alone-answer.ts — wrong when there genuinely is
// a spouse), and "add a household member" (add-household-member-
// answer.ts — right only when the spouse WILL eventually be entered).
//
// Andrea's report (andrea-planning-ops.md §8): a Medicare client is
// married, his wife is real and known, but she has never been a
// Stewardship contact and never will be — she isn't a client, doesn't
// intend to become one, and there is nothing to search for or add.
// None of the three existing answers is true. The nearest workaround
// was "No — lives alone", which clears the flag but writes a false
// fact onto the record: he does not live alone, and the wrongness
// would be visible to anyone who reads the household's history later.
//
// So this is a fourth, HONEST close: it uses the same dismiss path as
// lives-alone (no schema change, no new durable marker — the dismissed
// cleanup issue is itself the record of the decision, same as every
// other answer here), but says what is actually true instead of
// reusing a sentence that happens to also clear the flag.
//
// Same slug + single-household-ref gate as its siblings — the
// shared-phone shape already names a candidate household to merge
// into, and none of these three answers apply once a candidate exists.
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/
// without dragging web/src/api.ts (DOM-typed) into the root tsconfig
// program — same convention as the sibling predicate files here.

export interface SpouseNotAClientIssueLike {
  detectorSlug?: string | undefined;
  affectedRecords?: { kind: string; id: string; label: string }[] | undefined;
}

export interface SpouseNotAClientAnswer {
  /** Button copy — reads as the answer to the question, not as a verb. */
  label: string;
  /** Auto-filled rationale, so answering stays one click (UX rule 8). */
  rationale: string;
  /** Plain-English consequence shown under the button (UX rule 3). */
  consequence: string;
}

const SLUGS = new Set(["spouse-pass"]);

export function spouseNotAClientAnswer(
  issue: SpouseNotAClientIssueLike,
): SpouseNotAClientAnswer | null {
  if (!issue.detectorSlug || !SLUGS.has(issue.detectorSlug)) return null;
  const householdRefs = (issue.affectedRecords ?? []).filter(
    (r) => r.kind === "household",
  );
  if (householdRefs.length !== 1) return null;
  const person =
    (issue.affectedRecords ?? []).find((r) => r.kind === "contact")?.label ??
    null;
  return {
    label: person
      ? `No — ${person} is married, but their spouse won't be a client`
      : "No — married, but the spouse won't be a client",
    rationale:
      "Married; spouse is real and known but will never become a Stewardship contact.",
    consequence:
      "Nothing on the record changes. This household stops being flagged and won't be asked again.",
  };
}
